/**
 * Utility functions for formatting dates with timezone awareness for CaaS API
 */
import { addMonths, parse, startOfDay } from "date-fns";
import { format, formatInTimeZone, toZonedTime } from "date-fns-tz";
/**
 * Parses a date string in MM/dd/yyyy format robustly
 * This function ensures consistent date parsing across all browsers and locales
 *
 * @param dateString - The date string to parse in MM/dd/yyyy format (e.g., "01/15/2026")
 * @returns Date object with time set to start of day, or null if parsing fails
 *
 * @example
 * parseDateString("01/15/2026")
 * // Returns: Date object for January 15, 2026 at 00:00:00
 *
 * parseDateString("invalid")
 * // Returns: null
 */
export const parseDateString = (dateString) => {
    if (!dateString || typeof dateString !== "string") {
        return null;
    }
    try {
        // Use date-fns parse function for robust parsing
        // MM/dd/yyyy format: 01/15/2026
        const parsedDate = parse(dateString, "MM/dd/yyyy", new Date());
        // Check if the parsed date is valid
        if (isNaN(parsedDate.getTime())) {
            return null;
        }
        // Return date with time normalized to start of day
        return startOfDay(parsedDate);
    }
    catch (error) {
        console.error("Failed to parse date:", dateString, error);
        return null;
    }
};
/**
 * Formats a date for display in MM/dd/yyyy format, considering timezone offset
 *
 * @param date - The date to format
 * @param _timezoneOffset - Timezone offset in format like "UTC+01:00" or "+01:00" (reserved for future use)
 * @returns Formatted date string in MM/dd/yyyy format
 *
 * @example
 * formatDateWithTimezone(new Date(), "UTC+01:00")
 * // Returns: "01/13/2026"
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const formatDateWithTimezone = (date, _timezoneOffset) => {
    // For display purposes, we just format the date as-is
    // The timezone offset is mainly for API submission
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};
/**
 * Get the maximum allowed date for live view only links (today + next 1 day = 2 days total: Today and Tomorrow)
 *
 * @param isMaxTwoDays - Whether the link is configured for two days view
 * @param isMaxThreeMonths - Whether the link is configured for three months view
 * @returns Date string in yyyy/MM/dd format for max date, or undefined if no restriction
 *
 * @example
 * getMaxDateForLiveView(true, false)
 * // Returns: "2026/02/02" (if today is 2026-02-01, allows: Feb 1 (Today), Feb 2 (Tomorrow))
 *
 * getMaxDateForLiveView(false, true)
 * // Returns: "2026/05/01" (if today is 2026-02-01, allows through 3 calendar months)
 *
 * getMaxDateForLiveView(false, false)
 * // Returns: undefined (no restriction)
 */
export const getMaxDateForLiveView = (isMaxTwoDays, isMaxThreeMonths = false) => {
    if (isMaxTwoDays) {
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 1); // Today + next 1 day (2 days total: Today and Tomorrow)
        return format(maxDate, "yyyy/MM/dd");
    }
    else if (isMaxThreeMonths) {
        const maxDate = addMonths(new Date(), 3);
        return format(maxDate, "yyyy/MM/dd");
    }
    return undefined;
};
/**
 * Get today's date for minimum date restriction (prevents selecting past dates)
 *
 * @returns Date string in yyyy/MM/dd format for today's date
 *
 * @example
 * getTodayDate()
 * // Returns: "2026/02/01" (if today is February 1, 2026)
 */
export const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`; // Returns yyyy/MM/dd format
};
/**
 * Returns the minimum selectable date for link expiry.
 * Always returns today's date to prevent selecting past dates for all link types.
 * For Live View Only, max date is also restricted to today + next 2 days (3 days total).
 *
 * @returns Date string in yyyy/MM/dd format for today
 *
 * @example
 * getMinDateForLiveView()
 * // Returns: "2026/02/01" (today's date)
 */
export const getMinDateForLiveView = () => {
    // Reuse getTodayDate to avoid duplication
    return getTodayDate();
};
/**
 * Get today's date in the project's timezone
 *
 * @param timezoneOffset - Timezone offset like "UTC+01:00" or "+09:00"
 * @returns Date string in MM/dd/yyyy format for date picker's setActiveDate property
 *
 * @example
 * getTodayInProjectTimezone("UTC+01:00")
 * // Returns: "01/15/2026" (today in project timezone)
 */
export const getTodayInProjectTimezone = (timezoneOffset) => {
    const now = new Date();
    if (!timezoneOffset) {
        // Fallback to browser's local time
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const year = now.getFullYear();
        return `${month}/${day}/${year}`;
    }
    // Extract offset hours and minutes from format like "UTC+01:00" or "+09:00"
    const offsetStr = timezoneOffset.replace("UTC", "");
    const offsetMatch = offsetStr.match(/([+-])(\d{1,2}):?(\d{2})?/);
    if (!offsetMatch) {
        // Invalid format, fallback to browser time
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const year = now.getFullYear();
        return `${month}/${day}/${year}`;
    }
    const sign = offsetMatch[1] === "+" ? 1 : -1;
    const hours = parseInt(offsetMatch[2], 10);
    const minutes = parseInt(offsetMatch[3] || "0", 10);
    const totalOffsetMinutes = sign * (hours * 60 + minutes);
    // Get UTC time and apply project timezone offset
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
    const projectTime = new Date(utcTime + totalOffsetMinutes * 60000);
    const month = String(projectTime.getMonth() + 1).padStart(2, "0");
    const day = String(projectTime.getDate()).padStart(2, "0");
    const year = projectTime.getFullYear();
    return `${month}/${day}/${year}`; // Returns MM/dd/yyyy format
};
/**
 * Formats expiry date to MM/dd/yyyy format for API submission
 * Handles timezone offset extraction and formatting
 *
 * @param dateString - The date string to format (from date picker)
 * @param timezoneOffset - Timezone offset in format like "UTC+01:00" or "+01:00"
 * @returns Formatted date string in MM/dd/yyyy format (e.g., "01/15/2026")
 *
 * @example
 * formatExpiryDate("2026-01-15", "UTC+01:00")
 * // Returns: "01/15/2026"
 */
export const formatExpiryDate = (dateString, timezoneOffset) => {
    const expiryDateObj = new Date(dateString);
    const month = String(expiryDateObj.getMonth() + 1).padStart(2, "0");
    const day = String(expiryDateObj.getDate()).padStart(2, "0");
    const year = expiryDateObj.getFullYear();
    // Extract timezone offset (e.g., "+01:00" from "UTC+01:00")
    let offset = timezoneOffset;
    if (timezoneOffset.startsWith("UTC")) {
        offset = timezoneOffset.substring(3);
    }
    // Ensure proper format with colon (e.g., "+01:00" not "+0100")
    if (offset.match(/^[+-]\d{1,2}$/)) {
        const sign = offset[0];
        const hours = offset.substring(1).padStart(2, "0");
        offset = `${sign}${hours}:00`;
    }
    // Format as MM/dd/yyyy
    return `${month}/${day}/${year}`;
};
/**
 * Formats a Date object to ISO string with timezone offset format required by CaaS API
 *
 * IMPORTANT: This formats the date using UTC date/time components.
 * The API expects timestamps in the site's local timezone, so we use UTC components
 * and append the project's timezone offset to ensure consistent behavior across all browser timezones.
 *
 * For example, if "January 15, 2026 23:59:59" is selected for a site in Amsterdam (UTC+01:00):
 * - Browser creates: Date object (time depends on browser's timezone)
 * - We extract UTC components: 2026-01-15T10:59:59.999Z (assuming browser is UTC+13)
 * - We send: "2026-01-15T10:59:59.999+01:00" (using UTC components with project offset)
 *
 * @param date - The date to format (browser's Date object)
 * @param offset - Timezone offset in format like "UTC+01:00" or "+01:00"
 * @returns Formatted date string like "2023-01-01T00:00:00.000+01:00"
 *
 * @example
 * // Date created in browser, project is in Amsterdam (UTC+01:00)
 * formatDateWithTimezoneOffset(new Date('2026-01-15'), 'UTC+01:00')
 * // Returns: "2026-01-15T00:00:00.000+01:00" (using UTC components)
 */
export const formatDateWithTimezoneOffset = (date, offset) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        console.error("Invalid date provided to formatDateWithTimezoneOffset:", date);
        return new Date().toISOString().replace("Z", "+00:00");
    }
    // Extract UTC date/time components to ensure consistency across all browser timezones
    // This way "January 15, 2026" means the same thing regardless of where the user is located
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");
    const milliseconds = String(date.getUTCMilliseconds()).padStart(3, "0");
    // Extract timezone offset from the offset parameter
    // Handle formats: "UTC+01:00", "+01:00", "UTC+1", "+1"
    if (!offset) {
        // Default to UTC if no offset provided
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}+00:00`;
    }
    // Extract the offset part (e.g., "+01:00" from "UTC+01:00")
    let timezoneOffset = offset;
    if (offset.startsWith("UTC")) {
        timezoneOffset = offset.substring(3); // Remove "UTC" prefix
    }
    // Ensure proper format with colon (e.g., "+01:00" not "+0100")
    if (timezoneOffset.match(/^[+-]\d{1,2}$/)) {
        // Format like "+1" or "-5"
        const sign = timezoneOffset[0];
        const hours = timezoneOffset.substring(1).padStart(2, "0");
        timezoneOffset = `${sign}${hours}:00`;
    }
    else if (timezoneOffset.match(/^[+-]\d{4}$/)) {
        // Format like "+0100"
        const sign = timezoneOffset[0];
        const hours = timezoneOffset.substring(1, 3);
        const minutes = timezoneOffset.substring(3, 5);
        timezoneOffset = `${sign}${hours}:${minutes}`;
    }
    // Build ISO string with UTC time components + project timezone offset
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${timezoneOffset}`;
};
/**
 * Converts a Date object to UTC by treating its components as being in a specific timezone
 *
 * @param date - The date whose components (year, month, day, hour, etc.) should be interpreted as being in the target timezone
 * @param timezone - IANA timezone identifier (e.g., "Europe/Amsterdam", "America/New_York")
 * @returns UTC ISO string with Z suffix
 *
 * @example
 * // User selects Jan 7, 2026 00:00:00 (browser shows this)
 * // We want to treat this as Jan 7, 2026 00:00:00 Amsterdam time
 * const date = new Date(2026, 0, 7, 0, 0, 0);
 * convertTimezoneToUTC(date, "Europe/Amsterdam")
 * // Returns: "2026-01-06T23:00:00.000Z" (Amsterdam is UTC+1, so 00:00 Amsterdam = 23:00 UTC previous day)
 */
export const convertTimezoneToUTC = (date, timezone = "UTC") => {
    try {
        // Extract the date/time components as plain numbers
        const year = date.getFullYear();
        const month = date.getMonth(); // 0-indexed
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const milliseconds = date.getMilliseconds();
        // Use a trick: Create a formatter that outputs the date in the target timezone
        // Then parse it back to find the UTC equivalent
        const formatter = new Intl.DateTimeFormat("en-US", {
            day: "2-digit",
            hour: "2-digit",
            hour12: false,
            minute: "2-digit",
            month: "2-digit",
            second: "2-digit",
            timeZone: timezone,
            year: "numeric"
        });
        // We need to find what UTC time corresponds to our target time in the timezone
        // Strategy: Create a UTC date, see what it looks like in target TZ, adjust
        // Start with a rough UTC guess
        const utcGuess = Date.UTC(year, month, day, hours, minutes, seconds, milliseconds);
        // See what this UTC time looks like in the target timezone
        const parts = formatter.formatToParts(new Date(utcGuess));
        const tzYear = parseInt(parts.find((p) => p.type === "year")?.value || "0");
        const tzMonth = parseInt(parts.find((p) => p.type === "month")?.value || "0") - 1;
        const tzDay = parseInt(parts.find((p) => p.type === "day")?.value || "0");
        const tzHour = parseInt(parts.find((p) => p.type === "hour")?.value || "0");
        const tzMinute = parseInt(parts.find((p) => p.type === "minute")?.value || "0");
        const tzSecond = parseInt(parts.find((p) => p.type === "second")?.value || "0");
        // Calculate the difference between what we want and what we got
        // Note: We preserve milliseconds from the original since formatter doesn't handle them
        const wantedTzTime = Date.UTC(year, month, day, hours, minutes, seconds, 0); // Use 0 for ms in comparison
        const actualTzTime = Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, tzSecond, 0);
        const offset = wantedTzTime - actualTzTime;
        // Apply the offset to get the correct UTC time, then add back the milliseconds
        const correctUtcTime = utcGuess + offset;
        return new Date(correctUtcTime).toISOString();
    }
    catch (error) {
        console.error(`Error converting timezone ${timezone} to UTC:`, error);
        // Fallback to direct ISO conversion
        return date.toISOString();
    }
};
/**
 * Converts a Date object to UTC ISO string using LOCAL components (no timezone conversion)
 * Simply takes the year/month/day/hour/etc from the date and formats as UTC
 *
 * @param date - The date whose components should be used as-is
 * @returns UTC ISO string with Z suffix using local components
 *
 * @example
 * const date = new Date(2026, 0, 7, 0, 0, 0); // Jan 7, 2026 00:00:00
 * convertTimezoneToUTCSimple(date)
 * // Returns: "2026-01-07T00:00:00.000Z" (uses components as-is)
 */
export const convertTimezoneToUTCSimple = (date) => {
    try {
        // Extract the date/time components as plain numbers
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
        // Return ISO string with Z suffix (no timezone conversion)
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
    }
    catch (error) {
        console.error(`Error formatting date to UTC:`, error);
        return date.toISOString();
    }
};
/**
 * Creates a time range filter object for CaaS API requests
 *
 * @param fromDate - Start date
 * @param toDate - End date
 * @param offset - Timezone offset from project metadata
 * @returns Filter object with includeLeft, includeRight, left, and right properties
 *
 * @example
 * createTimeRangeFilter(
 *   new Date('2023-01-01'),
 *   new Date('2023-12-31'),
 *   'UTC+01:00'
 * )
 * // Returns: {
 * //   includeLeft: true,
 * //   includeRight: false,
 * //   left: "2023-01-01T00:00:00.000+01:00",
 * //   right: "2023-12-31T23:59:59.999+01:00"
 * // }
 */
export const createTimeRangeFilter = (fromDate, toDate, offset) => {
    return {
        includeLeft: true,
        includeRight: false,
        left: formatDateWithTimezoneOffset(fromDate, offset),
        right: formatDateWithTimezoneOffset(toDate, offset)
    };
};
/**
 * Validates and safely extracts the IANA timezone name from project details.
 * Filters out UTC offset strings (e.g., "UTC+01:00") which are not valid for date-fns-tz.
 *
 * @param timezone - The timezone value from projectDetails (could be IANA name or UTC offset)
 * @returns Valid IANA timezone name string, or undefined if invalid
 *
 * @example
 * getValidTimezone("Europe/Amsterdam") // Returns: "Europe/Amsterdam"
 * getValidTimezone("UTC+01:00") // Returns: undefined (offset, not IANA name)
 * getValidTimezone(undefined) // Returns: undefined
 * getValidTimezone(null) // Returns: undefined
 */
export const getValidTimezone = (timezone) => {
    // Type guard: must be a non-empty string
    if (typeof timezone !== "string" || timezone.trim().length === 0) {
        return undefined;
    }
    const trimmedTimezone = timezone.trim();
    // Reject UTC offset strings (pattern: UTC+HH:MM or UTC-HH:MM or +HH:MM or -HH:MM)
    // These are not IANA timezone identifiers and will fail with date-fns-tz
    if (/^(UTC)?[+-]\d{2}:\d{2}$/.test(trimmedTimezone)) {
        console.warn(`Timezone contains UTC offset instead of IANA name: "${trimmedTimezone}". Use offset field instead.`);
        return undefined;
    }
    // Additional validation: IANA timezones typically contain a slash (e.g., "Europe/Amsterdam", "America/New_York")
    // or are single words (e.g., "UTC", "GMT"). Reject anything that looks like an offset.
    if (/^[+-]/.test(trimmedTimezone)) {
        console.warn(`Timezone appears to be an offset: "${trimmedTimezone}". Use offset field instead.`);
        return undefined;
    }
    return trimmedTimezone;
};
/**
 * Safely converts a date to zoned time with proper error handling and timezone validation.
 * Handles edge cases like same-offset timezones that can trigger Intl.DateTimeFormat errors.
 *
 * @param date - The date to convert
 * @param timezone - The IANA timezone name (validated using getValidTimezone)
 * @returns The zoned date, or the original date if conversion fails
 *
 * @example
 * safeToZonedTime(new Date(), "Europe/Amsterdam")
 * // Returns: Date in Amsterdam timezone
 *
 * safeToZonedTime(new Date(), "UTC+01:00")
 * // Returns: Original date (invalid timezone silently handled)
 */
export const safeToZonedTime = (date, timezone) => {
    // Validate timezone
    const validTimezone = timezone ? getValidTimezone(timezone) : undefined;
    if (!validTimezone) {
        try {
            return toZonedTime(date, "UTC");
        }
        catch (error) {
            console.error("Failed to convert to UTC timezone:", error);
            return date;
        }
    }
    try {
        return toZonedTime(date, validTimezone);
    }
    catch (error) {
        console.warn(`Failed to convert to timezone "${validTimezone}": ${error instanceof Error ? error.message : String(error)}. Falling back to UTC.`, error);
        try {
            return toZonedTime(date, "UTC");
        }
        catch (utcError) {
            console.error("Failed to convert even to UTC:", utcError);
            return date;
        }
    }
};
/**
 * Safely formats a date with error handling. Never throws - always returns a formatted string.
 * Falls back gracefully if formatting fails.
 *
 * @param date - The date to format
 * @param formatStr - The format string (e.g., "dd MMM yyyy")
 * @returns Formatted date string, or current date if formatting fails
 *
 * @example
 * safeFormat(new Date(), "dd MMM yyyy")
 * // Returns: "21 Jan 2026"
 *
 * safeFormat(invalidDate, "dd MMM yyyy")
 * // Returns: "21 Jan 2026" (fallback)
 */
export const safeFormat = (date, formatStr) => {
    // Type guard
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        console.warn(`Invalid date provided to safeFormat: ${date}. Using current date as fallback.`);
        return format(new Date(), formatStr);
    }
    try {
        return format(date, formatStr);
    }
    catch (error) {
        console.error(`Failed to format date with pattern "${formatStr}": ${error instanceof Error ? error.message : String(error)}. Using fallback.`, error);
        try {
            // Fallback to simpler format
            return format(new Date(), formatStr);
        }
        catch (fallbackError) {
            // Ultimate fallback - return ISO string
            console.error("Even fallback formatting failed. Returning ISO string.", fallbackError);
            return new Date().toISOString();
        }
    }
};
/**
 * Safely formats a date in a specific timezone with error handling.
 * Never throws - always returns a formatted string or fallback.
 *
 * @param date - The date to format
 * @param timezone - The IANA timezone name (e.g., "Europe/Amsterdam")
 * @param formatStr - The format string (e.g., "yyyy-MM-dd")
 * @returns Formatted date string in the specified timezone, or current date if formatting fails
 *
 * @example
 * safeFormatInTimeZone(new Date(), "Europe/Amsterdam", "yyyy-MM-dd")
 * // Returns: "2026-01-21"
 *
 * safeFormatInTimeZone(new Date(), "InvalidTZ", "yyyy-MM-dd")
 * // Returns: "2026-01-21" (fallback to current date)
 */
export const safeFormatInTimeZone = (date, timezone, formatStr) => {
    // Type guards
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        console.warn(`Invalid date provided to safeFormatInTimeZone: ${date}. Using current date as fallback.`);
        return format(new Date(), formatStr);
    }
    // Validate timezone
    const validTimezone = timezone ? getValidTimezone(timezone) : undefined;
    if (!validTimezone) {
        console.debug(`No valid timezone in safeFormatInTimeZone. Using UTC as fallback.`);
        try {
            return formatInTimeZone(date, "UTC", formatStr);
        }
        catch (error) {
            console.warn("Failed to format in UTC timezone:", error);
            return format(date, formatStr);
        }
    }
    try {
        return formatInTimeZone(date, validTimezone, formatStr);
    }
    catch (error) {
        console.warn(`Failed to format in timezone "${validTimezone}": ${error instanceof Error ? error.message : String(error)}. Falling back to UTC.`, error);
        try {
            return formatInTimeZone(date, "UTC", formatStr);
        }
        catch (utcError) {
            console.error("Failed to format even in UTC:", utcError);
            return format(date, formatStr);
        }
    }
};
